import icons from '@/assets/icons';
import DatePickerComponent from '@/components/date-picker/date-picker';
import MarkdownTextTailwind from '@/components/markdown/markdown-text-tailwind';
import { ROUTE_TEST_DETAILS, ROUTE_TESTING_PLAN_COMPLETE } from '@/constants/routes';
import { useCompleteTestingPlan } from '@/queries/testing-plan.queries';
import { getRoute } from '@/utils';
import { useNavigate } from 'react-router-dom';
import { TestPlanScreeningType } from '../../types';
import useTestingPlanCardController from './testing-plan-card.controller';

type TestingPlanCardProps = {
  screeningTypeData: TestPlanScreeningType;
  hideCompleteTest?: boolean;
};

export default function TestingPlanCard({ screeningTypeData, hideCompleteTest }: TestingPlanCardProps) {
  const completeTestMutation = useCompleteTestingPlan();
  const testingSummary = useTestingPlanCardController(screeningTypeData);
  const navigate = useNavigate();

  if (!testingSummary) return null;

  const { name, what, why, testSuggestionId, targetDate, handleDateChange } = testingSummary;
  const { iconId } = screeningTypeData;

  return (
    <section className="dashboard-reset intervention-card-container">
      <div className="intervention-card-header">
        <div className="intervention-card-icon-container">
          <div className="intervention-card-icon-wrapper">
            <img src={iconId ? icons[iconId] : icons['biomarkers']} alt="" className="h-full w-full object-contain" />
          </div>
        </div>

        <div className="intervention-card-risk-badge">{screeningTypeData.name || 'Recommended'}</div>
      </div>

      <div className="intervention-card-content">
        <h2 className="intervention-card-title">{name}</h2>

        <DatePickerComponent initialDate={targetDate} onDateChange={handleDateChange} />

        <div className="testing-card-body">
          <MarkdownTextTailwind className="mb-2 text-[16px] leading-[1.3] text-[#183344]">
            {`**What:** ${what}`}
          </MarkdownTextTailwind>
          <MarkdownTextTailwind className="text-[16px] leading-[1.3] text-[#183344]">
            {`**Why:** ${why}`}
          </MarkdownTextTailwind>
        </div>
      </div>

      <div className="intervention-card-actions">
        <button
          onClick={() => {
            navigate(getRoute(ROUTE_TEST_DETAILS, { idTest: testSuggestionId }));
          }}
          className="intervention-card-btn-details"
          type="button"
        >
          Details
        </button>
        {!hideCompleteTest && (
          <button
            onClick={async () => {
              try {
                await completeTestMutation.mutateAsync({ recommendationId: testSuggestionId });
                navigate(getRoute(ROUTE_TESTING_PLAN_COMPLETE, { idTestOption: testSuggestionId }));
              } catch (error) {
                console.error('Failed to complete test:', error);
              }
            }}
            className="intervention-card-btn-complete"
            type="button"
            disabled={completeTestMutation.isPending}
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <path
                d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z"
                fill="currentColor"
              />
            </svg>
            {completeTestMutation.isPending ? 'Completing...' : 'Mark as Complete'}
          </button>
        )}
      </div>
    </section>
  );
}
